// Catálogo de alertas (PO1-2194, A6) — admin Everlog. Lista os alertas do motor
// e cria/edita cada um (sem exclusão: desativa por `status`). Molde:
// views/Admin/ConectoresCatalogo.
//
// Sem `?.`/`??`: o build roda em CRA 3.4 / Node 12.

import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Col, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, Row, Spinner, Table } from "reactstrap";

import PageHero, { PageHeroRefreshButton } from "../../../components/PageHero";
import ShellCard, { TableSurface } from "../../../components/ShellCard";
import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";
import { useToast } from "../../../hooks/toast";
import { atualizarCatalogo, criarCatalogo, listarCatalogo } from "../../../services/alertasApi";
import { listarTiposContatoEmbarcador } from "../../../services/embarcadorContatosApi";
import { CANAL_LABEL, FREQUENCIA_LABEL, PRODUTO_LABEL, TIPO_LABEL, mensagemErro } from "../../Alertas/alertasUtils";

const labelStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.75rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  marginBottom: "0.25rem",
};
const dicaStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.76rem", marginTop: "0.25rem" };
const mutedStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem" };

const PRODUTOS = ["FLOW", "GOBID", "TRACKFY", "AUTOPILOT", "MEUFRETE"];
const CANAIS = ["SINO", "EMAIL"];
const FREQUENCIAS = ["IMEDIATO", "RESUMO_DIARIO"];
const TIPOS = ["NOTIFICACAO", "TAREFA", "RELATORIO"];
// <select> não guarda null; "" representa "Todos os produtos" (produto = null).
const TODOS = "";
// Relatório (PO1-3384): só e-mail, na hora (um envio por embarcador por dia); o assunto e o
// corpo vêm do gerador, e os contatos do embarcador do tipo escolhido também recebem.
const RELATORIO = { canaisPermitidos: ["EMAIL"], frequenciasPermitidas: ["IMEDIATO"], frequenciaPadrao: "IMEDIATO" };
export const ehRelatorio = (tipo) => tipo === "RELATORIO";

const VAZIO = {
  id: null,
  codigo: "",
  nome: "",
  descricao: "",
  tipo: "NOTIFICACAO",
  produto: TODOS,
  canaisPermitidos: ["SINO"],
  frequenciasPermitidas: ["IMEDIATO"],
  frequenciaPadrao: "IMEDIATO",
  mensagemAlerta: "",
  mensagemEmail: "",
  assuntoEmail: "",
  link: "",
  status: true,
  idTipoContato: "",
};

function alternarNaLista(lista, valor, marcado) {
  const sem = (lista || []).filter((v) => v !== valor);
  return marcado ? sem.concat([valor]) : sem;
}

// Mantém a ordem canônica (SINO, EMAIL / IMEDIATO, RESUMO_DIARIO) no payload.
function ordenar(lista, ordem) {
  return ordem.filter((v) => (lista || []).indexOf(v) !== -1);
}

export function validarCatalogo(d) {
  if (!d.codigo.trim()) return "Informe o código.";
  if (!d.nome.trim()) return "Informe o nome.";
  if (!d.canaisPermitidos.length) return "Marque ao menos um canal.";
  if (!d.frequenciasPermitidas.length) return "Marque ao menos uma frequência.";
  if (d.frequenciasPermitidas.indexOf(d.frequenciaPadrao) === -1) return "A frequência padrão precisa estar entre as permitidas.";
  // Relatório: assunto e corpo vêm do gerador, não do catálogo.
  if (!ehRelatorio(d.tipo) && d.canaisPermitidos.indexOf("EMAIL") !== -1 && !d.assuntoEmail.trim()) return "Informe o assunto do e-mail.";
  return null;
}

// Ao virar Relatório, trava canal/frequência; ao sair, volta ao que o admin tinha.
export function aplicarTipo(form, tipo) {
  if (ehRelatorio(tipo)) return { ...form, tipo, ...RELATORIO };
  return { ...form, tipo, idTipoContato: "" };
}

export default function AlertasCatalogo() {
  const { addToast } = useToast();
  const [itens, setItens] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [form, setForm] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [tiposContato, setTiposContato] = useState([]);

  const flash = useCallback(
    (type, description) =>
      addToast({ place: "tr", type, title: type === "success" ? "OK" : "Atenção", description }),
    [addToast],
  );

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const data = await listarCatalogo();
      setItens((data && data.items) || []);
    } catch (e) {
      flash("danger", mensagemErro(e, "Falha ao carregar o catálogo de alertas (acesso admin?)."));
    } finally {
      setCarregando(false);
    }
  }, [flash]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Tipos de contato do embarcador (para o Relatório). Falha só esconde o select.
  useEffect(() => {
    let vivo = true;
    listarTiposContatoEmbarcador()
      .then((t) => {
        if (vivo) setTiposContato(Array.isArray(t) ? t : []);
      })
      .catch(() => {
        if (vivo) setTiposContato([]);
      });
    return () => {
      vivo = false;
    };
  }, []);

  const editar = (item) =>
    setForm({
      ...VAZIO,
      ...item,
      idTipoContato: item.idTipoContato || "",
      produto: item.produto || TODOS,
      descricao: item.descricao || "",
      mensagemAlerta: item.mensagemAlerta || "",
      mensagemEmail: item.mensagemEmail || "",
      assuntoEmail: item.assuntoEmail || "",
      link: item.link || "",
      canaisPermitidos: item.canaisPermitidos || [],
      frequenciasPermitidas: item.frequenciasPermitidas || [],
      status: !!item.status,
    });

  const set = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

  const trocarFrequencia = (freq, marcado) =>
    setForm((f) => {
      const frequenciasPermitidas = ordenar(alternarNaLista(f.frequenciasPermitidas, freq, marcado), FREQUENCIAS);
      const frequenciaPadrao =
        frequenciasPermitidas.indexOf(f.frequenciaPadrao) !== -1 ? f.frequenciaPadrao : frequenciasPermitidas[0] || "";
      return { ...f, frequenciasPermitidas, frequenciaPadrao };
    });

  const salvar = async () => {
    const erro = validarCatalogo(form);
    if (erro) {
      flash("danger", erro);
      return;
    }
    setSalvando(true);
    try {
      const payload = {
        codigo: form.codigo.trim(),
        nome: form.nome.trim(),
        descricao: form.descricao,
        tipo: form.tipo,
        produto: form.produto === TODOS ? null : form.produto,
        canaisPermitidos: ordenar(form.canaisPermitidos, CANAIS),
        frequenciasPermitidas: ordenar(form.frequenciasPermitidas, FREQUENCIAS),
        frequenciaPadrao: form.frequenciaPadrao,
        mensagemAlerta: form.mensagemAlerta,
        mensagemEmail: form.mensagemEmail,
        assuntoEmail: form.assuntoEmail,
        link: form.link.trim(),
        status: !!form.status,
        idTipoContato: ehRelatorio(form.tipo) && form.idTipoContato ? Number(form.idTipoContato) : null,
      };
      if (form.id) await atualizarCatalogo(form.id, payload);
      else await criarCatalogo(payload);
      flash("success", form.id ? "Alerta atualizado." : "Alerta criado.");
      setForm(null);
      carregar();
    } catch (e) {
      flash("danger", mensagemErro(e, "Falha ao salvar o alerta."));
    } finally {
      setSalvando(false);
    }
  };

  // Campo texto: captura o valor antes do setState (event pooling do React 16).
  const onTexto = (campo) => (e) => {
    const v = e.target.value;
    set(campo, v);
  };

  return (
    <div className="content">
      <PageHero
        eyebrow="Admin Everlog"
        title="Catálogo de alertas"
        description="Alertas e tarefas que o motor pode disparar: por produto, canais e frequências permitidos, textos do sino e do e-mail e o link de destino. Para tirar um alerta de uso, desative-o."
        actions={
          <>
            <PageHeroRefreshButton onClick={carregar} loading={carregando} />
            <Button color="info" className="btn-sm" onClick={() => setForm({ ...VAZIO })}>
              <i className="tim-icons icon-simple-add" /> Novo alerta
            </Button>
          </>
        }
      />

      <ShellCard title="Alertas cadastrados" action={<span style={mutedStyle}>{itens.length} no catálogo</span>}>
        <TableSurface>
          <div className="table-responsive">
            <Table className="text-left hub-thead-accent" hover style={{ color: AUTOPILOT_COLORS.text }}>
              <thead className="text-primary">
                <tr>
                  <th>Código</th>
                  <th>Nome</th>
                  <th>Produto</th>
                  <th>Tipo</th>
                  <th>Canais</th>
                  <th>Frequências</th>
                  <th>Ativo</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {itens.map((a) => (
                  <tr key={a.id}>
                    <td style={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{a.codigo}</td>
                    <td>
                      <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600 }}>{a.nome}</div>
                      <div style={mutedStyle}>{a.descricao || ""}</div>
                    </td>
                    <td>{a.produto ? PRODUTO_LABEL[a.produto] || a.produto : "Todos"}</td>
                    <td>{TIPO_LABEL[a.tipo] || a.tipo}</td>
                    <td>{(a.canaisPermitidos || []).map((c) => CANAL_LABEL[c] || c).join(", ")}</td>
                    <td>
                      {(a.frequenciasPermitidas || []).map((f) => (
                        <div key={f}>
                          {FREQUENCIA_LABEL[f] || f}
                          {f === a.frequenciaPadrao ? <span style={mutedStyle}> (padrão)</span> : null}
                        </div>
                      ))}
                    </td>
                    <td>
                      <Badge color={a.status ? "success" : "secondary"} pill>
                        {a.status ? "Ativo" : "Inativo"}
                      </Badge>
                    </td>
                    <td className="text-right">
                      <Button size="sm" color="secondary" outline onClick={() => editar(a)}>
                        Editar
                      </Button>
                    </td>
                  </tr>
                ))}
                {itens.length === 0 && !carregando ? (
                  <tr>
                    <td colSpan={8} style={{ ...mutedStyle, padding: "1.25rem" }}>
                      Nenhum alerta no catálogo.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </Table>
          </div>
        </TableSurface>
      </ShellCard>

      <Modal isOpen={!!form} toggle={() => setForm(null)} fade={false} size="lg" modalClassName="modal-align-top">
        <ModalBody style={{ padding: "1.5rem" }}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 700, marginBottom: "1rem" }}>
            {form && form.id ? "Editar alerta" : "Novo alerta"}
          </div>
          {form ? (
            <>
              <Row>
                <Col md="5">
                  <Label style={labelStyle}>Código</Label>
                  <Input value={form.codigo} disabled={!!form.id} placeholder="EX.: FATURA_DIVERGENTE" onChange={onTexto("codigo")} />
                </Col>
                <Col md="7">
                  <Label style={labelStyle}>Nome</Label>
                  <Input value={form.nome} onChange={onTexto("nome")} />
                </Col>
              </Row>
              <Label style={{ ...labelStyle, marginTop: "0.75rem" }}>Descrição</Label>
              <Input type="textarea" rows={2} value={form.descricao} onChange={onTexto("descricao")} />

              <Row style={{ marginTop: "0.75rem" }}>
                <Col md="6">
                  <Label style={labelStyle}>Produto</Label>
                  <Input type="select" value={form.produto} onChange={onTexto("produto")}>
                    <option value={TODOS}>Todos os produtos</option>
                    {PRODUTOS.map((p) => (
                      <option key={p} value={p}>
                        {PRODUTO_LABEL[p]}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md="6">
                  <Label style={labelStyle}>Tipo</Label>
                  <Input
                    type="select"
                    value={form.tipo}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm((f) => aplicarTipo(f, v));
                    }}
                  >
                    {TIPOS.map((t) => (
                      <option key={t} value={t}>
                        {TIPO_LABEL[t]}
                      </option>
                    ))}
                  </Input>
                </Col>
              </Row>

              {ehRelatorio(form.tipo) ? (
                <Row style={{ marginTop: "0.75rem" }}>
                  <Col md="6">
                    <Label style={labelStyle}>Tipo de contato que recebe</Label>
                    <Input type="select" value={form.idTipoContato} onChange={onTexto("idTipoContato")}>
                      <option value="">Só os usuários inscritos</option>
                      {tiposContato.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.nome}
                        </option>
                      ))}
                    </Input>
                    <div style={dicaStyle}>
                      Relatório: um e-mail por embarcador, uma vez por dia útil, com anexo. Vai para os usuários inscritos e para os
                      contatos do embarcador deste tipo (tela Contatos). Nunca para transportadora.
                    </div>
                  </Col>
                </Row>
              ) : null}

              <Row style={{ marginTop: "0.75rem" }}>
                <Col md="4">
                  <Label style={labelStyle}>Canais permitidos</Label>
                  {CANAIS.map((c) => (
                    <FormGroup check key={c}>
                      <Label check>
                        <Input
                          type="checkbox"
                          checked={form.canaisPermitidos.indexOf(c) !== -1}
                          disabled={ehRelatorio(form.tipo)}
                          onChange={(e) => {
                            const marcado = e.target.checked;
                            setForm((f) => ({
                              ...f,
                              canaisPermitidos: ordenar(alternarNaLista(f.canaisPermitidos, c, marcado), CANAIS),
                            }));
                          }}
                        />
                        <span className="form-check-sign">
                          <span className="check" />
                        </span>
                        {CANAL_LABEL[c]}
                      </Label>
                    </FormGroup>
                  ))}
                </Col>
                <Col md="4">
                  <Label style={labelStyle}>Frequências permitidas</Label>
                  {FREQUENCIAS.map((f) => (
                    <FormGroup check key={f}>
                      <Label check>
                        <Input
                          type="checkbox"
                          checked={form.frequenciasPermitidas.indexOf(f) !== -1}
                          disabled={ehRelatorio(form.tipo)}
                          onChange={(e) => {
                            const marcado = e.target.checked;
                            trocarFrequencia(f, marcado);
                          }}
                        />
                        <span className="form-check-sign">
                          <span className="check" />
                        </span>
                        {FREQUENCIA_LABEL[f]}
                      </Label>
                    </FormGroup>
                  ))}
                </Col>
                <Col md="4">
                  <Label style={labelStyle}>Frequência padrão</Label>
                  <Input
                    type="select"
                    value={form.frequenciaPadrao}
                    disabled={form.frequenciasPermitidas.length === 0}
                    onChange={onTexto("frequenciaPadrao")}
                  >
                    {form.frequenciasPermitidas.map((f) => (
                      <option key={f} value={f}>
                        {FREQUENCIA_LABEL[f]}
                      </option>
                    ))}
                  </Input>
                </Col>
              </Row>

              <Label style={{ ...labelStyle, marginTop: "0.75rem" }}>Mensagem do alerta (sino)</Label>
              <Input type="textarea" rows={2} value={form.mensagemAlerta} onChange={onTexto("mensagemAlerta")} />

              <Label style={{ ...labelStyle, marginTop: "0.75rem" }}>Assunto do e-mail</Label>
              <Input value={form.assuntoEmail} onChange={onTexto("assuntoEmail")} />

              <Label style={{ ...labelStyle, marginTop: "0.75rem" }}>Mensagem do e-mail</Label>
              <Input type="textarea" rows={3} value={form.mensagemEmail} onChange={onTexto("mensagemEmail")} />

              <Label style={{ ...labelStyle, marginTop: "0.75rem" }}>Link</Label>
              <Input value={form.link} placeholder="/app/{produto}/..." onChange={onTexto("link")} />
              <div style={dicaStyle}>
                Use {"{entidadeId}"} e {"{produto}"} para montar o caminho (ex.: /app/{"{produto}"}/operacao/faturas?abrir=
                {"{entidadeId}"}).
              </div>

              <FormGroup check style={{ marginTop: "0.9rem" }}>
                <Label check>
                  <Input
                    type="checkbox"
                    checked={!!form.status}
                    onChange={(e) => {
                      const marcado = e.target.checked;
                      set("status", marcado);
                    }}
                  />
                  <span className="form-check-sign">
                    <span className="check" />
                  </span>
                  Ativo
                </Label>
              </FormGroup>
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setForm(null)} disabled={salvando}>
            Cancelar
          </Button>
          <Button color="info" onClick={salvar} disabled={salvando}>
            {salvando ? <Spinner size="sm" /> : "Salvar"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
